import { Button, Radio, Stack, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import ModalV2 from '/@/components/bayer/modal-v2';
import { useCapabilities } from '/@/hooks/session';
import { _ } from '/@/locale';
import { getVisitReportEnabled } from '/@/store/session/selectors';
import type { FieldRegistryType } from '/@/store/tracking/events/field-registry';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import CreateRegistryApplication from './application';
import ClaimForm from './claim';
import {
    type FieldRegistryFlow,
    FieldRegistryFlowContext,
    flowEventProps,
    startFieldRegistryFlow,
    toRegistryType,
    trackFormOpened,
} from './flow-tracking';
import CreateRegistryNote from './note';
import CreateRegistryPlanting from './planting';
import type { RegistryAddStep } from './types';
import CreateRegistryVisitReport from './visitReport';

type RegistryType = Exclude<RegistryAddStep, 'select'>;

type RegistryOption = {
    type: RegistryType;
    title: string;
    subtitle: string;
    disabled?: boolean;
    hidden?: boolean;
};

/** Steps that open a form inside this modal; Cosecha and Estimación hand off to flows of their own. */
const FORM_STEPS: RegistryAddStep[] = ['planting', 'application', 'note', 'claim', 'visit'];

/** The visit's first screen is the field selection; the others open straight on their form. */
const formOpenedStep = (registryType: FieldRegistryType) =>
    registryType === 'visit' ? { step: 'field_selection' as const } : {};

const showRegistrySavedToast = () => {
    enqueueSnackbar(_('¡Listo! El registro se guardó en el seguimiento del lote.'), { variant: 'success' });
};

const AddRegistryModal = ({
    open,
    onClose,
    fieldId,
    cropMonitoring,
    initialStep = 'select',
    onHarvest,
    onYieldEstimation,
}: {
    open: boolean;
    onClose: () => void;
    fieldId?: number;
    cropMonitoring?: CropMonitoring;
    /** Skips the type picker and opens directly on this step, e.g. from a CTA specific to one registry type. */
    initialStep?: RegistryAddStep;
    /** "Cosecha" opens the existing harvest-report flow instead of a step of its own, in this attempt. */
    onHarvest?: (flow?: FieldRegistryFlow) => void;
    /** "Estimación de rendimiento" opens the existing yield-estimation drawer instead of a step of its own. */
    onYieldEstimation?: (flow?: FieldRegistryFlow) => void;
}) => {
    const { canCreateVisitReports } = useCapabilities();
    const visitReportAvailable = useSelector(getVisitReportEnabled);
    const allOptions: RegistryOption[] = [
        {
            type: 'planting',
            title: _('Siembra'),
            subtitle: _('Agregar cultivo, producto y densidad'),
            disabled: !!cropMonitoring?.id,
        },
        {
            type: 'application',
            title: _('Aplicaciones'),
            subtitle: _('Registrar protección de cultivos o fertilización'),
            disabled: cropMonitoring?.cornCropMonitoring?.phenology === 'harvested',
        },
        {
            type: 'visit',
            title: _('Informe'),
            subtitle: _('Crear un informe con observaciones de una visita, un reclamo o con datos del lote.'),
            disabled: cropMonitoring?.cornCropMonitoring?.phenology === 'harvested',
            // A visit TO a grower: only where the instance enables visit reports and
            // only for profiles that have growers.
            hidden: !visitReportAvailable || !canCreateVisitReports,
        },
        {
            type: 'note',
            title: _('Nota'),
            subtitle: _('Agregar una observación sobre el lote'),
            disabled: cropMonitoring?.cornCropMonitoring?.phenology === 'harvested',
        },
        {
            type: 'claim',
            title: _('Reclamo'),
            subtitle: _('Registrar un reclamo de semillas o químicos'),
            disabled: cropMonitoring?.cornCropMonitoring?.phenology === 'harvested',
        },
        {
            type: 'yieldEstimation',
            title: _('Estimación de rendimiento'),
            subtitle: _('Tomar muestras para estimar el rendimiento del lote'),
            // Corn-only, on an active (unharvested) campaign.
            disabled: cropMonitoring?.crop !== 'corn' || !cropMonitoring?.id || !!cropMonitoring?.harvestDate,
        },
        {
            type: 'harvest',
            title: _('Cosecha'),
            subtitle: _('Generar reporte con resultados y datos de la cosecha'),
            disabled: !cropMonitoring?.id || !!cropMonitoring?.harvestDate,
        },
    ];
    const OPTIONS = allOptions.filter((option) => !option.hidden);
    const [step, setStep] = useState<RegistryAddStep>(initialStep);
    const [selectedType, setSelectedType] = useState<RegistryType>('');
    const [flow, setFlow] = useState<FieldRegistryFlow>();

    // The modal stays mounted across opens (only `open` toggles), so re-apply `initialStep`
    // each time it's opened rather than only on first mount. Each open is a new attempt, too.
    // biome-ignore lint/correctness/useExhaustiveDependencies: the field and its crop are read once per open; a refetch mid-attempt must not start another
    useEffect(() => {
        if (open) {
            setStep(initialStep);
            if (initialStep !== 'select') setSelectedType(initialStep);
            const opensForm = FORM_STEPS.includes(initialStep);
            const started = startFieldRegistryFlow(
                { fieldId, cropMonitoring, origin: opensForm ? 'direct' : 'picker', entrySource: 'field-monitoring' },
                toRegistryType(initialStep) ?? 'select'
            );
            const registryType = toRegistryType(initialStep);
            if (opensForm && registryType) trackFormOpened(started, registryType, formOpenedStep(registryType));
            setFlow(started);
        }
    }, [open, initialStep]);

    const handleClose = () => {
        setStep('select');
        setSelectedType('');
        onClose();
    };

    const handleRegistrySaved = () => {
        showRegistrySavedToast();
        handleClose();
    };

    const handleContinue = () => {
        const option = OPTIONS.find((o) => o.type === selectedType);
        if (option?.disabled) return;
        const registryType = toRegistryType(selectedType);
        if (flow && registryType)
            mpTrackEvent('Field Registry Type Selected', { ...flowEventProps(flow), registry_type: registryType });
        if (selectedType === 'harvest') {
            handleClose();
            onHarvest?.(flow);
            return;
        }
        if (selectedType === 'yieldEstimation') {
            handleClose();
            onYieldEstimation?.(flow);
            return;
        }
        setStep(selectedType);
        if (flow && registryType && FORM_STEPS.includes(selectedType))
            trackFormOpened(flow, registryType, formOpenedStep(registryType));
    };

    if (step === 'note') {
        return (
            <CreateRegistryNote
                open={open}
                onClose={handleClose}
                onSuccess={handleRegistrySaved}
                setStep={setStep}
                fieldId={fieldId}
                cropMonitoring={cropMonitoring}
            />
        );
    }

    if (step === 'claim') {
        return (
            <FieldRegistryFlowContext.Provider value={flow}>
                <ClaimForm
                    open={open}
                    onClose={handleClose}
                    onSuccess={handleRegistrySaved}
                    goBack={() => setStep('select')}
                    fieldId={fieldId}
                    cropMonitoring={cropMonitoring}
                />
            </FieldRegistryFlowContext.Provider>
        );
    }

    if (step === 'planting') {
        return (
            <CreateRegistryPlanting
                open={open}
                onClose={handleClose}
                onSuccess={handleRegistrySaved}
                setStep={setStep}
                fieldId={fieldId}
                cropMonitoring={cropMonitoring}
            />
        );
    }

    if (step === 'application') {
        return (
            <FieldRegistryFlowContext.Provider value={flow}>
                <CreateRegistryApplication
                    open={open}
                    onClose={handleClose}
                    onSuccess={handleRegistrySaved}
                    setStep={setStep}
                    fieldId={fieldId}
                />
            </FieldRegistryFlowContext.Provider>
        );
    }

    if (step === 'visit') {
        return (
            <FieldRegistryFlowContext.Provider value={flow}>
                <CreateRegistryVisitReport
                    open={open}
                    onClose={handleClose}
                    setStep={setStep}
                    fieldId={fieldId}
                    cropMonitoring={cropMonitoring}
                />
            </FieldRegistryFlowContext.Provider>
        );
    }

    return (
        <ModalV2
            open={open}
            onClose={handleClose}
            title={_('Seleccioná qué querés registrar')}
            bodyProps={{ alignItems: 'stretch', textAlign: 'left', spacing: 2 }}
            actionsProps={{ justifyContent: 'flex-end' }}
            fullScreenOnMobile
            actions={
                <Stack direction={'row'} spacing={1.5}>
                    <Button
                        variant='text'
                        color='secondary'
                        onClick={handleClose}
                        sx={{ width: { xs: '100%', md: 'auto' } }}
                    >
                        {_('Cancelar')}
                    </Button>
                    <Button
                        variant='contained'
                        color='secondary'
                        onClick={handleContinue}
                        sx={{ width: { xs: '100%', md: 'auto' } }}
                    >
                        {_('Continuar')}
                    </Button>
                </Stack>
            }
        >
            <Stack spacing={1}>
                {OPTIONS.map((option) => {
                    const isSelected = selectedType === option.type;
                    return (
                        <Stack
                            key={option.type}
                            direction='row'
                            alignItems='center'
                            justifyContent='space-between'
                            spacing={2}
                            onClick={() => !option.disabled && setSelectedType(option.type)}
                            sx={{
                                border: 1,
                                borderColor: isSelected ? 'secondary.main' : 'primary.mui.stroke',
                                borderRadius: 2,
                                p: 2,
                                cursor: option.disabled ? 'default' : 'pointer',
                                opacity: option.disabled ? 0.5 : 1,
                            }}
                        >
                            <Stack spacing={0.5}>
                                <Typography variant='h6' color='primary'>
                                    {option.title}
                                </Typography>
                                <Typography variant='body2' color='primary.mui.inactive'>
                                    {option.subtitle}
                                </Typography>
                            </Stack>
                            <Radio
                                checked={isSelected}
                                disabled={option.disabled}
                                color='secondary'
                                onChange={() => setSelectedType(option.type)}
                            />
                        </Stack>
                    );
                })}
            </Stack>
        </ModalV2>
    );
};

export default AddRegistryModal;
